import React, { useEffect } from 'react';
import { ClinicalGuides, GenerationMode, ModelVersion, V2OutputMode } from '../App';
import RefineImageViewer from './refine/RefineImageViewer';
import RefineSidebar from './refine/RefineSidebar';

interface RefineScreenProps {
    originalImage: { file: File, url: string };
    isUpscaled: boolean;
    handleUpscaleImage: () => void;
    setIsGumLineEditorOpen: (isOpen: boolean) => void;
    gumLinePath: { x: number; y: number }[] | null;
    setIsClinicalGuideEditorOpen: (isOpen: boolean) => void;
    clinicalGuides: ClinicalGuides | null;
    setIsMouthAnnotatorOpen: (isOpen: boolean) => void;
    mouthPath: { x: number; y: number }[] | null;
    setIsCroppingOneToOne: (isOpen: boolean) => void;
    treatmentType: 'cosmetic' | 'orthodontic' | 'restorative' | 'pro-automated' | 'fast-pro';
    setTreatmentType: (type: 'cosmetic' | 'orthodontic' | 'restorative' | 'pro-automated' | 'fast-pro') => void;
    isAutomaticPlanning: boolean;
    setIsAutomaticPlanning: (isAuto: boolean) => void;
    selectedStyles: string[];
    setSelectedStyles: (styles: string[]) => void;
    selectedOrthoStyle: string;
    setSelectedOrthoStyle: (style: string) => void;
    customPrompt: string;
    setCustomPrompt: (prompt: string) => void;
    handleGenerate: () => void;
    isLoading: boolean;
    handleReset: () => void;
    isAIDentistEnabled: boolean;
    setIsAIDentistEnabled: (enabled: boolean) => void;
    generationMode: GenerationMode;
    setGenerationMode: (mode: GenerationMode) => void;
    patientName: string;
    setPatientName: (name: string) => void;
    // V2 Props
    modelVersion: ModelVersion;
    setModelVersion: (version: ModelVersion) => void;
    v2OutputMode: V2OutputMode;
    setV2OutputMode: (mode: V2OutputMode) => void;
    v2CosmeticStyle: 'natural' | 'hollywood';
    setV2CosmeticStyle: (style: 'natural' | 'hollywood') => void;
}

const RefineScreen: React.FC<RefineScreenProps> = (props) => {
    const {
        originalImage, isUpscaled, handleUpscaleImage, setIsGumLineEditorOpen, gumLinePath,
        setIsClinicalGuideEditorOpen, clinicalGuides, setIsMouthAnnotatorOpen, mouthPath,
        setIsCroppingOneToOne, treatmentType, setTreatmentType, isAutomaticPlanning, setIsAutomaticPlanning,
        selectedStyles, setSelectedStyles, selectedOrthoStyle, setSelectedOrthoStyle,
        customPrompt, setCustomPrompt, handleGenerate, isLoading, handleReset,
        isAIDentistEnabled, setIsAIDentistEnabled, generationMode, setGenerationMode,
        patientName, setPatientName,
        modelVersion, setModelVersion, v2OutputMode, setV2OutputMode,
        v2CosmeticStyle, setV2CosmeticStyle,
    } = props;

    // Logic to reset generation mode if multi-select is used
    useEffect(() => {
        if (treatmentType === 'cosmetic' && !isAutomaticPlanning && selectedStyles.length > 1) {
            setGenerationMode('per-style');
        }
    }, [selectedStyles, treatmentType, isAutomaticPlanning, setGenerationMode]);

    // Reset selections when treatment type changes
    useEffect(() => {
        setSelectedStyles(['Natural Enhanced']);
        setSelectedOrthoStyle('Final Aligned Result');
        setGenerationMode('per-style');
    }, [treatmentType]);

    // Reset to valid treatment type when switching to V2
    useEffect(() => {
        if (modelVersion === 'v2' && !['cosmetic', 'orthodontic', 'restorative'].includes(treatmentType)) {
            setTreatmentType('cosmetic');
        }
    }, [modelVersion, treatmentType, setTreatmentType]);

    return (
        <div className="relative w-full h-full flex flex-col lg:flex-row items-stretch p-4 sm:p-6 lg:p-8 gap-6 lg:gap-8 overflow-hidden bg-zinc-50 dark:bg-zinc-950 transition-colors duration-500">
            {/* Ambient Background Effects */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] rounded-full bg-teal-500/10 blur-[120px] animate-pulse" style={{ animationDuration: '8s' }} />
                <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] rounded-full bg-indigo-500/10 blur-[120px] animate-pulse" style={{ animationDuration: '10s', animationDelay: '1s' }} />
                <div className="absolute top-[40%] left-[40%] w-[30%] h-[30%] rounded-full bg-gold-500/5 blur-[100px] animate-pulse" style={{ animationDuration: '12s', animationDelay: '2s' }} />
            </div>

            {/* Left Panel: Image Viewer */}
            <div className="relative z-10 flex-1 min-h-0">
                <RefineImageViewer
                    originalImage={originalImage}
                    isUpscaled={isUpscaled}
                    handleUpscaleImage={handleUpscaleImage}
                    setIsGumLineEditorOpen={setIsGumLineEditorOpen}
                    gumLinePath={gumLinePath}
                    setIsClinicalGuideEditorOpen={setIsClinicalGuideEditorOpen}
                    clinicalGuides={clinicalGuides}
                    setIsMouthAnnotatorOpen={setIsMouthAnnotatorOpen}
                    mouthPath={mouthPath}
                    setIsCroppingOneToOne={setIsCroppingOneToOne}
                />
            </div>

            {/* Right Panel: Controls */}
            <div className="relative z-20 w-full lg:w-[420px] xl:w-[480px] flex-shrink-0 h-full">
                <RefineSidebar
                    patientName={patientName}
                    setPatientName={setPatientName}
                    treatmentType={treatmentType}
                    setTreatmentType={setTreatmentType}
                    isAutomaticPlanning={isAutomaticPlanning}
                    setIsAutomaticPlanning={setIsAutomaticPlanning}
                    selectedStyles={selectedStyles}
                    setSelectedStyles={setSelectedStyles}
                    selectedOrthoStyle={selectedOrthoStyle}
                    setSelectedOrthoStyle={setSelectedOrthoStyle}
                    generationMode={generationMode}
                    setGenerationMode={setGenerationMode}
                    isAIDentistEnabled={isAIDentistEnabled}
                    setIsAIDentistEnabled={setIsAIDentistEnabled}
                    customPrompt={customPrompt}
                    setCustomPrompt={setCustomPrompt}
                    handleGenerate={handleGenerate}
                    isLoading={isLoading}
                    handleReset={handleReset}
                    mouthPath={mouthPath}
                    modelVersion={modelVersion}
                    setModelVersion={setModelVersion}
                    v2OutputMode={v2OutputMode}
                    setV2OutputMode={setV2OutputMode}
                    v2CosmeticStyle={v2CosmeticStyle}
                    setV2CosmeticStyle={setV2CosmeticStyle}
                />
            </div>
        </div>
    );
};

export default RefineScreen;